// src/utils/eventCreationHelpers.js
// UPDATED VERSION - H2H and Showdown now use tournaments collection for unified flow
// Changes marked with // UNIFIED_CHANGE

import { Alert } from 'react-native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Timestamp } from 'firebase/firestore';
import { GAME_PROFILES } from '../constants/gameData';
import { buildCommunityGameProfile } from '../constants/communityGames';

/**
 * Builds the required stream title for an event.
 * NOTE: This is now deprecated in favor of makeBaseStreamTitle from streamTitle.js
 */
export function buildStreamTitle({ gameKey, name, startAt, entryFeeCents, shortId, category }) {
  const when = startAt?.toDate ? startAt.toDate() : new Date();
  const hh = String(when.getHours()).padStart(2, '0');
  const mm = String(when.getMinutes()).padStart(2, '0');
  return [
    'CritCash',
    GAME_PROFILES[gameKey]?.abbr || GAME_PROFILES[gameKey]?.displayName || gameKey,
    String(category || 'event').toUpperCase(),
    entryFeeCents ? `$${(entryFeeCents / 100).toFixed(2)}` : null,
    `${hh}:${mm}`,
    shortId ? `EVT-${shortId}` : null,
  ].filter(Boolean).join(' • ');
}

/**
 * Handles the server-side event creation process.
 * UPDATED: H2H and Showdown now use tournaments collection for unified infrastructure
 */
export async function handleCreateEvent({
  // Auth & State Setters
  user,
  isAdmin,
  setBusy,
  setSuccessData,
  setRosterModalOpen,
  payoutStructures,
  
  // Event Details from State
  mode,
  game,
  gameName,         // Open catalog: free-text name when game === 'other' (backend slugs it)
  gameGenre,        // Open catalog: declared genre for 'other' games (category + modes)
  category,
  formatName,
  teamSize,
  entryUnit,
  entryFeeCents,
  critEntryCost,    // Post-pivot Crit entry cost (whole Crits); entryFeeCents stays 0
  platform,
  crossplay,        // Explicit crossplay flag
  visibility,       // 'public' or 'private'
  bracketSize,
  ladderWeeks,
  gridSize,         // For racing/showdown events (showdown: format-aware lobby cap)
  heatCount,        // For racing events
  entriesExpected,  // Calculated expected entrants
  // Warzone battle royale (showdown chassis) - see gameData battleRoyale formats
  battleRoyale,     // true = single-lobby BR FFA event (Warzone playlists)
  squadSize,        // in-game squad size 1-4 (entries stay per-player, teamSize 1)
  minEntrants,      // the GAME's start floor - under it at lock = auto-cancel + refund
  startMode,        // 'relative' | 'absolute' | 'fill'
  startInMins,      // for 'relative'
  fillDelayMins,    // for 'fill' — minutes after fill before start
  roundScheduleMode,    // 'gap' | 'scheduled' — inter-round timing (multi-round only)
  interRoundGapMinutes, // gap-mode minutes between rounds
  customTitle,          // paid custom event/stream title (moderated server-side)
  showError,            // ErrorContext dialog — REQUIRED for web; Alert.alert is a no-op there
  onMembershipRequired, // called instead of a plain error when the members-only gate rejects
  onInsufficientCrits,  // called with { have, need } when the balance can't cover the create cost
  creatorRoomId,    // optional, H2H+GT7 only — pre-submitted Room ID
  dayOffset,
  absHour,
  absMinute,
  eventName,        // Structured event name (base stream title)
  requiredStreamTitle, // Base stream title for detection
  creatorRole,      // 'host' or 'racer' (GT7 host model)
  hostRaces,        // GT7 showdown: host also races (16th seat, full grid)
  hostSelectedRounds = [],  // COD host model: rounds the creator will host (1 match each)
  rosterUids = [],
  // GT7-specific fields
  selectedTracks,    // Array of track name strings
  carCategory,       // 'gr1'|'gr2'|'gr3'|'gr4'
  lobbySettings,     // GT7_LOBBY_SETTINGS object (or undefined)
  // Madden in-game settings (MADDEN_GAME_SETTINGS shape, or undefined)
  gameSettings,
}) {
  // Cross-platform dialog: react-native's Alert.alert renders NOTHING on web,
  // so every failure here was silent in the browser until showError was wired.
  const notify = (title, message) => (
    typeof showError === 'function' ? showError(title, message) : Alert.alert(title, message)
  );

  if (!user) return notify('Sign in required', 'Sign in to create an event.');
  setBusy(true);
  try {
    // UNIFIED_CHANGE: H2H and Showdown now use tournaments collection
    // This allows them to leverage the full tournament infrastructure:
    // - Automatic seeding (populates p1/p2)
    // - Lobby credential generation
    // - Ready System initialization
    // - Vision verification
    // - Payout processing
    const collectionKey = 'tournaments'; // All event types use tournaments now

    // Compute timing per start mode. Registration always closes 15 minutes
    // before start for relative/absolute. For 'fill', timing is server-stamped
    // when the event reaches capacity, so client sends nothing.
    const REGISTRATION_CLOSE_LEAD_MIN = 15;
    let startAt = null;
    let registrationClosesAt = null;
    const normalizedStartMode = startMode === 'rel'
      ? 'relative'
      : startMode === 'abs'
        ? 'absolute'
        : (startMode || 'relative');

    if (normalizedStartMode === 'relative') {
      const mins = startInMins || 30;
      const startDate = new Date(Date.now() + mins * 60 * 1000);
      startAt = Timestamp.fromDate(startDate);
      const close = new Date(startDate.getTime() - REGISTRATION_CLOSE_LEAD_MIN * 60 * 1000);
      registrationClosesAt = Timestamp.fromDate(close);
    } else if (normalizedStartMode === 'absolute') {
      const d = new Date();
      d.setDate(d.getDate() + (dayOffset || 0));
      d.setHours(absHour || 0, absMinute || 0, 0, 0);
      startAt = Timestamp.fromDate(d);
      const close = new Date(d.getTime() - REGISTRATION_CLOSE_LEAD_MIN * 60 * 1000);
      registrationClosesAt = Timestamp.fromDate(close);
    }
    // 'fill' → leave startAt/registrationClosesAt null; server fills on capacity

    // Determine payout structure
    let payoutStructureId = null;
    if (mode === 'h2h') {
      payoutStructureId = 'h2h-winner-take-all';
    } else if (mode === 'bracket') {
      const size = bracketSize || entriesExpected || 16;
      const kMap = { 
        1024:'1,024', 2048:'2,048', 4096:'4,096', 8192:'8,192', 16384:'16,384' 
      };
      let candidate = kMap[size] ? `bracket-${kMap[size]}-team` : `bracket-${size}-team`;
      
      if (!payoutStructures.some(p => p.id === candidate)) {
        const fallbacksDesc = [
          'bracket-16k-team','bracket-8k-team','bracket-4k-team','bracket-2k-team',
          'bracket-1k-team','bracket-512-team','bracket-256-team','bracket-128-team',
          'bracket-64-team','bracket-32-team','bracket-16-team','bracket-8-team',
          'bracket-4-team','bracket-2-team'
        ];
        candidate = fallbacksDesc.find(id => payoutStructures.some(p => p.id === id)) || 'bracket-64-team';
      }
      payoutStructureId = candidate;
    } else if (mode === 'showdown') {
      if (battleRoyale) {
        // Warzone battle royale: static wz structures picked by squad size.
        // Mirrors the backend fallback in resolvePayoutStructureId (events.js).
        payoutStructureId = (Math.floor(Number(squadSize)) || 1) > 1
          ? 'wz-squads-top-5'
          : 'wz-solos-top-10';
      } else {
        // Racing showdown payout structure based on game only. Structure-id
        // casing is per-game (GT7 uppercase, the rest lowercase) — keep in
        // sync with CreateEventsScreen's map. Open-catalog racing games
        // (mkw, f25) have no GAME_PROFILES entry: their tuned grid comes
        // from the community profile (12 / 20), and the backend re-derives
        // and validates the same id server-side.
        const gameCodeMap = {
          'gt7': 'GT7',
          'fzh': 'fzh',
          'mkw': 'mkw',
          'irc': 'irc',
          'f25': 'f25',
        };
        const gameKey = gameCodeMap[game.toLowerCase()] || game.toUpperCase();
        const gameGridSize = GAME_PROFILES[game]?.maxPlayers
          || buildCommunityGameProfile(game).maxPlayers
          || 16;
        const topCount = Math.floor(gameGridSize / 2);
        payoutStructureId = `racing-${gameKey}-top-${topCount}`;
      }
    } else if (mode === 'racing') {
      // Racing heats structures are keyed by HEAT COUNT, mirroring the server
      // fallback in functions/modules/events.js resolvePayoutStructureId
      // (racing-{GAME}-{heatsCount}). The old lookup here keyed on ENTRANT
      // count ('racing-GT7-30' never exists) and then walked a largest-first
      // fallback list — dead code only because the structure objects carry no
      // `id` field, and one added `id` away from silently stamping every
      // heats event with the 16k structure (totalRounds 15).
      // GT7 is the only game with the full racing-GT7-{N} heats family (and
      // the only uppercase key). Other racing games have partial catalogs, so
      // stamp NOTHING and let the server fallback — which owns the whole
      // catalog — resolve the id (a wrong stamp here mispays via the
      // bracket-derived fallback at settlement).
      const kMap = {
        16384: '16k', 8192: '8k', 4096: '4k', 2048: '2k', 1024: '1k',
        512: '512', 256: '256', 128: '128', 64: '64', 32: '32',
        16: '16', 8: '8', 4: '4', 2: '2'
      };
      const hc = Number(heatCount) || 2;
      payoutStructureId = (game === 'gt7' && kMap[hc]) ? `racing-GT7-${kMap[hc]}` : null;
    } else if (mode === 'ladder') {
      const ladder = payoutStructures.find(p => p.id.startsWith('season-ladder-'));
      payoutStructureId = ladder?.id || 'season-ladder-100';
    }
    
    // Use the structured event name (base stream title) as the event name
    const finalEventName = eventName || requiredStreamTitle || `${GAME_PROFILES[game]?.displayName || game} Event`;
    
    // Build base event object
    const base = {
      name: finalEventName,
      kind: isAdmin ? 'official' : 'community',
      visibility: visibility || 'public',
      mode,
      game,
      // Open catalog: rides with game:'other' so the backend can slug it to a
      // shared cg_* key (stripped server-side for catalog games).
      ...(gameName ? { gameName } : {}),
      ...(gameGenre ? { gameGenre } : {}),
      platform: platform || GAME_PROFILES[game]?.allowedPlatforms?.[0] || 'Cross-Platform',
      crossplay: crossplay !== undefined ? crossplay : ((platform || '').toLowerCase() === 'cross-platform'),
      region: 'Global',
      entryFeeCents, // 0 under the Crit model — entry is paid in Crits
      critEntryCost: Math.max(0, Math.floor(Number(critEntryCost) || 0)),
      entryUnit,
      teamSize,
      formatName,
      payoutStructureId,
      status: 'scheduled',
      startMode: normalizedStartMode,
      ...(normalizedStartMode === 'relative' ? { startInMins: startInMins || 30 } : {}),
      ...(normalizedStartMode === 'fill' ? { fillDelayMins: fillDelayMins || 15 } : {}),
      // Inter-round timing — backend (validateRoundSchedule) stores these only
      // for multi-round events and ignores them for single-round.
      ...(interRoundGapMinutes ? { roundScheduleMode: roundScheduleMode || 'gap', interRoundGapMinutes } : {}),
      // Paid custom title — server moderates (profanity/game-name/trademark via
      // Claude) and charges the fee; creation FAILS on rejection, nothing charged.
      ...(customTitle && String(customTitle).trim() ? { customTitle: String(customTitle).trim() } : {}),
      ...(startAt ? { startAt } : {}),
      ...(registrationClosesAt ? { registrationClosesAt } : {}),
      ...(creatorRoomId ? { creatorRoomId } : {}),
      createdByUid: user.uid,
      requiredStreamTitle: requiredStreamTitle || finalEventName,
      requiresStreaming: true,
      
      // UNIFIED_CHANGE: H2H mode-specific fields
      // H2H is now a 2-player bracket tournament with 1 round
      ...(mode === 'h2h' ? {
        bracketSize: 2,
        totalRounds: 1,
        entriesExpected: 2,
        seeding: 'random',
        isH2H: true,  // Flag for UI to identify H2H events
        teamEntryMode: teamSize > 1 ? 'team' : 'solo',
      } : {}),
      
      // Bracket mode (unchanged)
      ...(mode === 'bracket' ? { 
        bracketSize: bracketSize || entriesExpected, 
        entriesExpected: entriesExpected || bracketSize, 
        seeding: 'random',
        teamEntryMode: teamSize > 1 ? 'team' : 'solo',
        allowPickupGroups: teamSize > 1,
      } : {}),
      
      // UNIFIED_CHANGE: Showdown mode-specific fields
      // Showdown is now a tournament with gridSize players in a single-match
      // "bracket". Format-aware: the caller's gridSize/entriesExpected win
      // (Warzone BR formats carry a 44-50 lobby cap); the game-profile
      // maxPlayers fallback keeps the GT7 path's values identical (15).
      ...(mode === 'showdown' ? (() => {
        const grid = Math.floor(Number(gridSize)) || GAME_PROFILES[game]?.maxPlayers || 15;
        const expected = Math.floor(Number(entriesExpected)) || grid;
        return {
          gridSize: grid,
          totalRounds: 1,
          heatsCount: 1,
          entriesExpected: expected,
          maxEntrants: expected,
          isShowdown: true,  // Flag for UI to identify Showdown events
          seeding: 'random',
          // Battle royale: NO bracketSize - the backend must never seed
          // bracket/team machinery off a 50-player single-lobby event.
          ...(battleRoyale ? {
            battleRoyale: true,
            squadSize: Math.max(1, Math.floor(Number(squadSize)) || 1),
            ...(Math.floor(Number(minEntrants)) >= 2
              ? { minEntrants: Math.floor(Number(minEntrants)) }
              : {}),
          } : { bracketSize: grid }),
        };
      })() : {}),
      
      // Racing mode (multi-heat)
      ...(mode === 'racing' ? { 
        gridSize: gridSize || GAME_PROFILES[game]?.maxPlayers || 15,
        heatsCount: heatCount || 1,
        entriesExpected: entriesExpected || (gridSize * heatCount),
        maxEntrants: entriesExpected || (gridSize * heatCount),
      } : {}),
      
      // Ladder mode
      ...(mode === 'ladder' ? {
        seasonWeeks: ladderWeeks || 4
      } : {}),

      // Host model (GT7): creator hosts; hostRaces means they ALSO take the
      // extra lobby seat as a racer (full grid) instead of spectating.
      ...(creatorRole === 'host' ? {
        creatorRole: 'host',
        hostUid: user.uid,
        ...(hostRaces === true ? { hostRaces: true } : {}),
      } : {}),

      // GT7 game settings (tracks, category, lobby settings)
      ...(selectedTracks?.length > 0 ? { tracks: selectedTracks } : {}),
      ...(carCategory ? { carCategory } : {}),
      ...(lobbySettings ? { lobbySettings } : {}),

      // Madden in-game settings — server re-sanitizes against the schema
      ...(gameSettings ? { gameSettings } : {}),

      // Format-derived settings (laps, pit stops from gameData.js format metadata)
      ...((() => {
        const formatData = GAME_PROFILES[game]?.formats?.[formatName];
        if (!formatData) return {};
        const extra = {};
        if (formatData.laps !== undefined) extra.laps = formatData.laps;
        if (formatData.minPitStops !== undefined) extra.minPitStops = formatData.minPitStops;
        if (formatData.requiredTireTypeChange !== undefined) extra.requiredTireTypeChange = formatData.requiredTireTypeChange;
        return extra;
      })()),
    };

    // Call Cloud Function to create event
    const isHostCreating = creatorRole === 'host';
    const functions = getFunctions(undefined, 'us-central1');
    const callCreate = httpsCallable(functions, 'createEventWithHold');
    const res = await callCreate({
      collection: collectionKey,
      payload: base,
      category,
      chargeOnCreate: !isAdmin && !isHostCreating,
      rosterUids,
    });

    const d = res?.data || {};
    if (!d.eventId || !d.collection) {
      throw new Error('Create response missing event info from server.');
    }

    // COD host model: if the creator chose to host, register their selected
    // rounds (one match per round) via the existing self-elect host system.
    // Non-fatal — the event already exists if this step fails.
    if (isHostCreating && Array.isArray(hostSelectedRounds) && hostSelectedRounds.length > 0) {
      try {
        const callJoinHost = httpsCallable(functions, 'joinEventAsHost');
        await callJoinHost({
          eventId: d.eventId,
          selectedRounds: hostSelectedRounds,
          hostTosAccepted: true,
        });
      } catch (hostErr) {
        console.error('Host round registration failed:', hostErr);
        notify(
          'Event created — host setup needs attention',
          `Your event was created, but registering your host rounds failed: ${hostErr?.message || hostErr}\n\nYou can set them from the event page.`
        );
      }
    }

    if (setRosterModalOpen) setRosterModalOpen(false);
    setSuccessData({
      eventId: d.eventId,
      collection: d.collection,
      requiredStreamTitle: d.requiredStreamTitle,
    });
      
  } catch (err) {
    console.error('Create failed:', err);
    const msg = String(err?.message || err);
    // Paid-plan creation gate -> upgrade call-to-action, not a dead-end error.
    // Match the stable code prefix from events.js ("size-requires-membership:")
    // so rewording the server prose never breaks the funnel; the legacy phrase
    // match stays as a fallback.
    if ((msg.includes('size-requires-membership') || msg.includes('member feature'))
      && typeof onMembershipRequired === 'function') {
      onMembershipRequired();
    } else
    // Balance short -> Crit-pack call-to-action with the exact shortfall.
    // Server message: "insufficient-crits:Not enough Crits. You have 60, this event costs 175."
    if (msg.includes('insufficient-crits') && typeof onInsufficientCrits === 'function') {
      const m = msg.match(/You have (\d+).*?costs (\d+)/);
      onInsufficientCrits(m ? { have: Number(m[1]), need: Number(m[2]) } : null);
    } else
    // Server title moderation rejections come back as "title-not-approved:<reason>"
    if (msg.includes('title-not-approved:')) {
      notify('Not approved title', msg.split('title-not-approved:')[1].trim()
        + '\n\nNothing was charged. Edit the title and try again.');
    } else {
      notify('Could not create', msg);
    }
  } finally {
    setBusy(false);
  }
}